import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';

/**
 * Superflex League Formula
 * Based on market data showing ~7-10 QBs in top 24 picks
 * Implements WAR (Wins Above Replacement) methodology
 */

export interface SuperflexFormulaWeights {
  previousPPG: number;
  projectedPoints: number;
  qbFloor: number;          // QB weekly floor matters more
  rushingQbBonus: number;   // Dual-threat QB premium
  consistency: number;
  targetShare: number;
  positionScarcity: number; // Moderate QB premium, not extreme
  teamOffense: number;
  injuryRisk: number;
  ageDecline: number;
}

export const SUPERFLEX_WEIGHTS: SuperflexFormulaWeights = {
  previousPPG: 0.22,        // Proven production
  projectedPoints: 0.20,    // Current projections
  qbFloor: 0.18,           // QB floor matters in SF
  rushingQbBonus: 0.12,    // Rushing QBs get boost
  consistency: 0.10,       // Reduced vs standard
  targetShare: 0.08,       // Less important than QB factors
  positionScarcity: 0.06,  // MODERATE QB premium (not extreme)
  teamOffense: 0.02,       // Team context
  injuryRisk: 0.01,        // Less weight
  ageDecline: 0.01         // Less weight in redraft SF
};

// Market-based replacement levels (based on 12-team SF ADP data)
const SUPERFLEX_REPLACEMENT_LEVELS = {
  QB: 22,  // QB22 is replacement (24 QBs for 24 starting spots)
  RB: 30,  // RB30 is replacement  
  WR: 36,  // WR36 is replacement
  TE: 15,  // TE15 is replacement
  K: 12,   // K12 is replacement
  DEF: 12  // DEF12 is replacement
};

export function calculateSuperflexValue(
  player: Player,
  leagueConfig: LeagueConfig
): number {
  const weights = SUPERFLEX_WEIGHTS;
  
  // Base factors
  const previousPPG = (player.lastYearPoints || 0) / 17;
  const projectedPoints = player.projection || player.projectedPoints || 0;
  const qbFloor = calculateQBFloor(player);
  const rushingQbBonus = calculateRushingQBBonus(player);
  const consistency = calculateConsistency(player);
  const targetShare = calculateTargetShare(player);
  const positionScarcity = calculateSuperflexScarcity(player);
  const teamOffense = calculateTeamOffense(player);
  const injuryRisk = calculateInjuryRisk(player);
  const ageDecline = calculateAgeDecline(player);
  
  // Calculate base value
  let superflexValue = 
    (weights.previousPPG * previousPPG) +
    (weights.projectedPoints * projectedPoints) +
    (weights.qbFloor * qbFloor) +
    (weights.rushingQbBonus * rushingQbBonus) +
    (weights.consistency * consistency) +
    (weights.targetShare * targetShare) +
    (weights.positionScarcity * positionScarcity) +
    (weights.teamOffense * teamOffense) +
    (weights.injuryRisk * injuryRisk) +
    (weights.ageDecline * ageDecline);
  
  // Apply WAR (Wins Above Replacement) methodology
  const warMultiplier = calculateWAR(player, projectedPoints);
  superflexValue *= warMultiplier;
  
  // Market sanity check - prevent too many QBs in top 24
  if (player.position === 'QB') {
    superflexValue *= getQBSaturationMultiplier(player);
  }
  
  return superflexValue;
}

function calculateQBFloor(player: Player): number {
  if (player.position !== 'QB') return 0;
  
  // QBs with high floors get bonus (rushing ability, team commitment)
  const rushingYards = 400; // Mock - would use real data
  const passingAttempts = 550; // Mock - would use real data
  
  const floorScore = Math.min(1.0, (rushingYards / 500) + (passingAttempts / 600));
  return floorScore;
}

function calculateRushingQBBonus(player: Player): number {
  if (player.position !== 'QB') return 0;
  
  // Bonus for dual-threat QBs (Allen, Hurts, Lamar, Daniels)
  const rushingQBs = ['Josh Allen', 'Jalen Hurts', 'Lamar Jackson', 'Jayden Daniels', 'Anthony Richardson'];
  
  if (rushingQBs.some(name => player.name.includes(name.split(' ')[1]))) {
    return 1.2; // 20% bonus for rushing QBs
  }
  
  return 1.0;
}

function calculateSuperflexScarcity(player: Player): number {
  // MUCH MORE AGGRESSIVE QB premium to get 8-9 QBs in top 24
  const scarcity = {
    QB: 2.5,  // VERY HIGH QB premium (was 1.9, now much higher)
    RB: 1.1,  // RBs less valuable (reduced further) 
    WR: 1.0,  // WRs baseline (reduced further)
    TE: 1.2,  // Elite TEs valuable (reduced)
    K: 0.7,   // Less important
    DEF: 0.8  // Less important
  };
  
  return scarcity[player.position as keyof typeof scarcity] || 1.0;
}

function calculateWAR(player: Player, projectedPoints: number): number {
  const position = player.position;
  const replacementLevel = SUPERFLEX_REPLACEMENT_LEVELS[position as keyof typeof SUPERFLEX_REPLACEMENT_LEVELS] || 24;
  
  // Calculate points above replacement
  const replacementPoints = getReplacementPoints(position);
  const pointsAboveReplacement = Math.max(0, projectedPoints - replacementPoints);
  
  // Convert to WAR multiplier (diminishing returns)
  const warMultiplier = 1.0 + (pointsAboveReplacement / 100);
  
  return Math.min(2.0, warMultiplier); // Cap at 2x
}

function getReplacementPoints(position: string): number {
  // Estimated replacement-level points by position
  const replacementPoints = {
    QB: 280,  // QB22 level
    RB: 180,  // RB30 level
    WR: 160,  // WR36 level
    TE: 120,  // TE15 level
    K: 100,   // K12 level
    DEF: 90   // DEF12 level
  };
  
  return replacementPoints[position as keyof typeof replacementPoints] || 100;
}

function getQBSaturationMultiplier(player: Player): number {
  // VERY PERMISSIVE QB saturation - allow 10-12 QBs in top 24  
  const qbRank = getQBRank(player);
  
  if (qbRank <= 12) return 1.0;     // Top 12 QBs - no penalty at all
  if (qbRank <= 16) return 0.95;    // QB13-16 - tiny penalty
  if (qbRank <= 20) return 0.85;    // QB17-20 - moderate penalty
  return 0.6;                       // QB21+ - significant penalty
}

function getQBRank(player: Player): number {
  // Expanded QB ranking to allow more QBs in top 24
  const topQBs = [
    'Josh Allen', 'Lamar Jackson', 'Jalen Hurts', 'Patrick Mahomes', 'Joe Burrow', 
    'Jayden Daniels', 'Dak Prescott', 'Tua Tagovailoa', 'CJ Stroud', 'Brock Purdy',
    'Trevor Lawrence', 'Jordan Love', 'Anthony Richardson'
  ];
  const index = topQBs.findIndex(name => player.name.includes(name.split(' ')[1]));
  return index >= 0 ? index + 1 : 20;
}

// Helper functions (shared with standard formula)
function calculateConsistency(player: Player): number {
  const baseConsistency = { QB: 0.8, RB: 0.6, WR: 0.5, TE: 0.7, K: 0.4, DEF: 0.5 };
  return baseConsistency[player.position as keyof typeof baseConsistency] || 0.5;
}

function calculateTargetShare(player: Player): number {
  const baseShares = { QB: 1.0, RB: 0.15, WR: 0.20, TE: 0.15, K: 0, DEF: 0 };
  return baseShares[player.position as keyof typeof baseShares] || 0;
}

function calculateTeamOffense(player: Player): number {
  const strongOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA', 'CIN'];
  return strongOffenses.includes(player.team) ? 1.2 : 1.0;
}

function calculateInjuryRisk(player: Player): number {
  const positionRisk = { QB: 0.9, RB: 0.7, WR: 0.8, TE: 0.8, K: 0.95, DEF: 0.9 };
  const ageRisk = (player.age || 26) > 30 ? 0.85 : 1.0;
  return (positionRisk[player.position as keyof typeof positionRisk] || 0.8) * ageRisk;
}

function calculateAgeDecline(player: Player): number {
  const age = player.age || 26;
  if (age < 24) return 1.05; // Small youth bonus in redraft SF
  if (age < 27) return 1.0;  // Prime
  if (age < 30) return 0.98; // Very slight decline
  return 0.95; // Mild decline (less than standard)
}

export function getSuperflexPositionPriority(): string[] {
  // Based on market data: mix of QBs and skill positions
  return ['QB', 'RB', 'WR', 'QB', 'RB', 'WR', 'TE', 'QB'];
}

export function getSuperflexDraftStrategy(): string[] {
  return [
    'Target 2-3 QBs total, but not at expense of all elite skill players',
    'Elite rushing QBs (Allen, Hurts, Lamar) have highest floors',
    'CMC, Saquon, Bijan still belong in first 2 rounds',
    'Elite WRs (Chase, Jefferson, Lamb) mix with QBs in rounds 1-3',
    'Kelce/LaPorta type TEs provide positional advantage',
    'Don\'t chase mid-tier QBs over elite skill players',
    'QB depth is better than in 1-QB, can wait on QB3'
  ];
}